<template>
  <div>
    <v-skeleton-loader
      v-if="loading"
      class="m-auto"
      type="table"
      transition="scale-transition"
    />
    <VTableRemote
      v-if="hasPermission('productContainers.view')"
      :iconHeader="hasPermission('productContainers.add') ? 'add' : null"
      :items="displayedProductsContainer"
      :headers="tableHeaders"
      :centerColumns="['action']"
      :itemActions="actions"
      :title="$t('productContainers.pageName')"
      :pagination="pagination"
      :sortableColumns="['name']"
      :notSortableColumns="['action']"
      hideDefaultFooter
      isCustomIcon
      @iconAction="onClickAction"
      @click-icon-header="onClickAdd"
      @sendQueryParams="fetchQuery"
    />
  </div>
</template>

<script>

import Vue from 'vue';
import VTableRemote from '@/components/shared/content-views/VTableRemote';
import { mapGetters } from 'vuex';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import ProductContainerForm from './ProductContainerForm.vue';


export default {
    components: {
        VTableRemote,
    },
    data() {
        return {
            queryParams: {
                search: '',
            },
            pagination: {},
            productContainers: [],
            loading: false,
        };
    },
    computed: {
        ...mapGetters([
            'hasPermission',
            'userLang',
            'teamMainLang',
        ]),
        tableHeaders() {
            return [
                'name',
            ].map(header => ({
                text: this.$t(`forms.labels.${header}`),
                value: header,
            }));
        },
        displayedProductsContainer() {
            return this.productContainers.map(container => ({
                id: container.id,
                name: container.name[this.userLang] || container.name[this.teamMainLang],
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('productContainers.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('productContainers.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        await this.getProductContainers();
    },
    methods: {
        async getProductContainers() {
            if (this.loading) return;
            this.loading = true;
            const cleanQueryParams = this.$utils.removeEmptyNullOrUndefined({ ...this.queryParams }, true);
            const { data } = await this.$rq.getProductContainers(cleanQueryParams);
            this.productContainers = [...this.productContainers, ...data.productContainers];
            this.loading = false;
        },
        onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('productContainers.add'),
                component: Vue.component('ProductContainerForm', ProductContainerForm),
                showLangs: true,
                onConfirm: requestPayload => this.addProductContainer(requestPayload),
            });
        },
        async onClickAction(item, action) {
            if (item === 'add') {
                await this.onClickAdd();
            } else if (action.mode === 'edit') {
                const { data } = await this.$rq.getProductContainer(item.id);
                this.$root.$emit('showModal', {
                    title: this.$t('productContainers.edit'),
                    component: Vue.component('ProductContainerForm', ProductContainerForm),
                    showLangs: true,
                    payload: { isEdition: true, productContainer: data },
                    onConfirm: requestPayload => this.updateProductContainer(item, requestPayload),
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('productContainers.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('productContainers.confirmRemove') },
                    onConfirm: () => this.deleteProductContainer(item),
                    isForm: false,
                });
            }
        },
        preparePayload(requestPayload) {
            const payload = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);
            return payload;
        },
        async addProductContainer(requestPayload) {
            const payload = this.preparePayload(requestPayload);
            await this.$rq.addProductContainer(payload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.productContainerAdded'),
            });
            this.productContainers = [];
            await this.getProductContainers();
        },
        async updateProductContainer({ id, name }, requestPayload) {
            if (!requestPayload) return;
            const properPayload = this.preparePayload(requestPayload);
            await this.$rq.updateProductContainer(id, properPayload);
            this.$toasted.global.toast_success({
                message: this.$t('msg.productContainerModified', { data: name[this.teamMainLang] }),
            });
            this.productContainers = [];
            await this.getProductContainers();
        },
        async deleteProductContainer({ id, name }) {
            await this.$rq.deleteProductContainer(id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.productContainerRemoved', { data: name[this.teamMainLang] }),
            });
            this.productContainers = [];
            await this.getProductContainers();
        },
        async fetchQuery(queryParams) {
            this.queryParams = { ...this.queryParams, ...queryParams, page: 1 };
            this.productContainers = [];
            await this.getProductContainers();
        },
    },
};

</script>
